import { MAX_NUMBER_OF_PAGES, MIN_NUMBER_OF_PAGES } from 'common/utils/bookSize.constants'
import { ART_BOOK } from 'artkive/constants/tracker/mainNavigation'
import { defaultHowItWorks } from 'artkive/stores/ecom/constants/data'

import About1 from 'images/ecom/books/Books-Focus-1.png'
import IntroGrandparents from 'images/ecom/books/Books-Intro-Grandparents.jpg'
import IntroParents from 'images/ecom/books/Books-Intro-Parents.jpg'
import IntroTeachers from 'images/ecom/books/Books-Intro-Teachers.jpg'
import Keepsake from 'images/ecom/books/Books-Keepsake.jpg'
import PileAfter from 'images/ecom/books/Books-Pile-After.jpg'
import PileBefore from 'images/ecom/books/Books-Pile-Before.jpg'
import WhoItsForGrandparents from 'images/ecom/books/Books-WhoItsFor-Grandparents.jpg'
import WhoItsForMilestones from 'images/ecom/books/Books-WhoItsFor-Milestones.jpg'
import WhoItsForParents from 'images/ecom/books/Books-WhoItsFor-Parents.jpg'
import About2 from 'images/ecom/books/BooksFocus2.jpg'
import About3 from 'images/ecom/books/BooksFocus3.jpg'

export const FAQS = [
  {
    id: 1,
    title: 'How many pieces of art fit in a book?',
    body:
      `You can print from ${MIN_NUMBER_OF_PAGES} to ${MAX_NUMBER_OF_PAGES} pages in a single book, ` +
      'so it fits one school term or several years of saved art. By default, each page holds 1 piece of art, ' +
      'like a gallery art book. To fit more art in one book, try our new page layouts (in beta), which let ' +
      'you combine a few pieces on a single page. Want to see one? [View a sample book](#preview).',
  },
  {
    id: 2,
    title: 'Can I put more than one piece of art on a page?',
    body: 'Each piece is photographed on its own, so by default every drawing gets its own page. Want a few on one page? Try our new page layouts (in beta) when you edit your book online. You can combine several pieces on a single page. You can also collage small pieces onto one sheet of paper before you send in your art.',
  },
  {
    id: 3,
    title: 'Can I customize my book?',
    body: 'Yes. Our book designers lay out your book for you, and once we’ve sent your proof you can reorder pages, change the cover and title, choose which details print (child name, age/grade, date) and add captions before it goes to print.',
  },
  {
    id: 4,
    title: 'How long does it take to print and ship?',
    body: 'Printing takes 4 business days, with standard shipping of 2-5 business days depending on location.',
  },
  {
    id: 5,
    title: 'How does payment work?',
    body: 'You purchase the Artkive Box first, then we photograph everything you send and invoice you based on the number of pieces and books selected. Please see the pricing slider above to get an estimate per book.',
  },
  {
    id: 6,
    title: 'Is this a memory book or a photo book?',
    body: 'It’s a keepsake made specifically for kids’ art, not a generic photo book template, so every layout is built around showing off a drawing, not a photograph.',
  },
  {
    id: 7,
    title: 'How long will the book actually last?',
    body: 'The archival paper and binding are built to resist yellowing and wear, so it’s designed to be opened again in twenty years, not just this year.',
  },
  {
    id: 8,
    title: 'What age is this good for?',
    body: 'From a toddler’s first scribbles to school-age projects, any child’s art works, and books can be added to over multiple years.',
  },
  {
    id: 9,
    title: 'Is this a good gift for grandparents?',
    body: 'Yes. It’s one of our most-gifted formats for grandparents, who often keep it on display and revisit it at every family visit.',
  },
]

export const bookProductBullets = [
  { text: 'Archival-quality printing built to outlast the pile in the closet' },
  { text: 'Sized to shelve, not stash: a real book, not a bin of paper' },
  { text: 'Every page dated and captioned, so the memory stays with the art' },
  { text: 'Free digital copy included, so the memories can never be lost' },
  {
    text:
      `Books scale from ${MIN_NUMBER_OF_PAGES} to ${MAX_NUMBER_OF_PAGES} pages, ` +
      'so one school year or five fits the same format',
  },
]

export const audienceCards = {
  intro: [
    "A kids art book is more than a place to put finished drawings, it's how a whole year of your child's creativity becomes something you actually keep. Instead of a drawer of curling pages or a phone full of photos you'll never look at again, every piece gets professionally photographed into an archival-quality book built to survive decades on a shelf.",
  ],
  cards: [
    {
      title: 'Parents',
      body: 'Parents use it to finally solve the school-year pile without the guilt of throwing anything away.',
      image: IntroParents,
      alt: 'A mom and her two kids on the couch holding up a rainbow painting',
    },
    {
      title: 'Grandparents',
      body: 'Grandparents ask to borrow it at every visit.',
      image: IntroGrandparents,
      alt: 'Grandparents paging through a Kids Art Book',
    },
    {
      title: 'Teachers | and Caregivers',
      body: 'Teachers and caregivers gift it at the end of a big year, a move, or a milestone.',
      image: IntroTeachers,
      alt: 'A teacher handing a Kids Art Book to a student',
    },
  ],
  outro:
    `Whether you're working through a single school term or five years of saved art, the book scales from ` +
    `${MIN_NUMBER_OF_PAGES} to ${MAX_NUMBER_OF_PAGES} pages, so the format fits the amount of creativity ` +
    "you're actually trying to keep, not the other way around.",
}

export const beforeAfter = {
  title: "Before it's a keepsake, | it's a pile.",
  body: "On the fridge. In a drawer. In a box you keep meaning to sort. You can't keep every drawing, and you shouldn't feel guilty about that. You're not throwing the memory away. You're just keeping it in a better way: one book, easy to hold, impossible to lose.",
  before: {
    image: PileBefore,
    alt: 'A child sorting through a box piled with drawings and paintings',
  },
  after: {
    image: PileAfter,
    alt: 'A finished Kids Art Book standing on a shelf beside a plant',
  },
  cta: {
    label: 'See How It Works',
    href: '#how-it-works',
    trackerEvent: ART_BOOK.SEE_HOW_IT_WORKS_CLICK,
    section: 'books_before_after',
  },
}

export const keepsakeQuote = {
  title: 'Made to Be Opened | Again in Twenty Years.',
  body: 'This is bound, archival quality, and built to survive moves and time. Parents keep it on the shelf. Grandparents ask to borrow it. Someday, your kid pulls it down for their own kid.',
  image: {
    image: Keepsake,
    alt: 'A mom and her two kids on the couch holding up their artwork and the printed Kids Art Book',
  },
  quote: {
    text: "I was nervous about losing my older son's art work, now 25 years old, when digital was not as common back then, so all I had was paper art that started fading. The Artkive team took care of it, and now my memories are lasting longer.",
    author: 'Nadiuska I.',
  },
  cta: { label: 'Get My Box', section: 'books_keepsake' },
}

export const productFeatures = [
  {
    title: 'Customizable cover, | in two sizes',
    titleWidth: 224,
    mobileTitleWidth: 382,
    body: '8"x8" and 11"x8.5", so the book fits your shelf.',
    image: About1,
    alt: 'Two Kids Art Books in the square and landscape sizes',
  },
  {
    title: 'Artwork stands out | on premium matte pages',
    titleWidth: 348,
    mobileTitleWidth: 382,
    body: "Archival paper that won't yellow, fade, or fingerprint over time.",
    image: About2,
    alt: 'An open Kids Art Book showing a drawing on a matte page',
  },
  {
    title: 'Custom captions | on every piece | if added',
    titleWidth: 302,
    mobileTitleWidth: 382,
    body: 'The story behind the scribble, saved right next to it.',
    image: About3,
    alt: 'A Kids Art Book page with a caption printed beside the artwork',
  },
]

export const whoItsFor = {
  title: "Who It's For",
  subtitle: 'Made for the person who keeps everything',
  cta: { label: 'Get My Box', section: 'books_who_its_for' },
}

export const recipientGroups = [
  {
    title: 'Grandparents',
    body: "A window into their grandkids' creativity, and a connection to their lives, especially from afar. Fits in a mailbox, sits on a coffee table, and gets pulled out at every visit: a better answer than another framed photo.",
    image: WhoItsForGrandparents,
    alt: 'Grandparents reading a Kids Art Book together',
    size: 'large',
  },
  {
    title: 'Parents',
    body: 'For the drawer you keep meaning to deal with. One book replaces a year (or five) of loose pages, guilt-free.',
    image: WhoItsForParents,
    alt: 'A dad and his two kids holding up their Kids Art Books',
    size: 'small',
  },
  {
    title: 'Milestones',
    body: 'Back-to-school, end-of-year, a big move. A natural close to a chapter, and a natural gift for the teacher who kept it all organized.',
    image: WhoItsForMilestones,
    alt: 'A Kids Art Book wrapped as a gift',
    size: 'small',
  },
]

export const testimonialUser = {
  name: 'Sara G.',
}

export const imageBgPosition = {
  xs: '50% 0%',
  sm: '50% 15%',
}

// The shared How It Works steps, with the phrase breaks the keepsake titles keep (see Keepsakes/Phrases). Keyed by the
// shared title, so a reordered or new step keeps its own title.
const HOW_IT_WORKS_TITLES = {
  'Order an Artkive Box\n & Send Us Your Art': 'Order | an Artkive Box | & Send Us Your Art',
  'Professional Photography & Editing': 'Professional | Photography | & Editing',
  'Make Final Payment': 'Make | Final Payment',
  'Design & Enjoy': 'Design | & Enjoy',
}

export const howItWorksSteps = defaultHowItWorks.map((step) => ({
  ...step,
  title: HOW_IT_WORKS_TITLES[step.title] ?? step.title,
}))
